import { toastNotifications } from '@uag/react-core';
import { t } from 'i18next';
import { Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid';

import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import {
    mapArticleInstanceToPlacement,
    mapPlacementToArticleInstance
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { isMultiPOISelection } from 'app/modules/wallforming/components/scenes/objects/articles/utils/clipboardUtils';
import { updateInstanceToPoiIndexMap } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { SelectionBox } from 'app/modules/wallforming/components/scenes/objects/helpers/SelectionBox';
import { getRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { ShortcutSection } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutSection';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { MAIN_DIV_ID } from 'shared/constants';

import type { GroupMember } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupMember';
import type { BoxSelectionResult } from 'app/modules/wallforming/components/scenes/objects/helpers/types/boxSelectionResult';
import type { SceneEventHandlerSliceState } from 'app/modules/wallforming/components/state-management/placement/states/sceneEventHandlerSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

let isBoxSelectionActiveFlag = false;

const ndcPosition = new Vector3();

const boxSelectionStartThreshold = 5;
let boxSelectionStartClientX = 0;
let boxSelectionStartClientY = 0;

let animationFrameId: number | null = null;

function computeNDCPosition(x: number, y: number, domElement: HTMLCanvasElement): Vector3 {
    const rect = domElement.getBoundingClientRect();
    ndcPosition.set(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1, 0);
    return ndcPosition;
}

function applyBoxSelection(state: PlacementState, isRecursiveSelection: boolean): void {
    if (!state.selectionBox || !state.mainWebGLRendererRef.current || !state.mainCameraControlsRef.current) {
        return;
    }

    state.syncMainCameras(state.mainWebGLRendererRef.current.domElement);
    state.selectionBox.select();

    const boxSelectionResult: BoxSelectionResult = {};

    if (state.isBatchModeActive()) {
        const batchUUIDToMap: Record<string, Map<number, string>> = {};
        const { physical: physicalBatch, sheet: sheetBatch, holes: holesBatch } = state.batchedMeshes;

        if (physicalBatch) {
            batchUUIDToMap[physicalBatch.uuid] = state.physicalBatchToInstance;
        }
        if (sheetBatch) {
            batchUUIDToMap[sheetBatch.uuid] = state.sheetBatchToInstance;
        }
        if (holesBatch) {
            batchUUIDToMap[holesBatch.uuid] = state.holesBatchToInstance;
        }

        let poiRelatedIds: Set<string> | null = null;

        if (!isRecursiveSelection) {
            const poiIndex = state.poiSelection.current.index;
            if (poiIndex === undefined) {
                return;
            }
            const selectedPOI = state.pois[poiIndex];
            if (!selectedPOI) {
                return;
            }

            poiRelatedIds = new Set<string>(Object.values(selectedPOI.articles).flat());
        }

        Object.entries(state.selectionBox.batches).forEach(([batchUUID, batchIds]) => {
            const reverseMap = batchUUIDToMap[batchUUID];
            if (!reverseMap || batchIds.length === 0) {
                return;
            }

            // Pre-compute instance-key-to-index maps to avoid repeated Object.keys + indexOf
            const articleIndexCache: Record<string, Map<string, number>> = {};

            batchIds.forEach((batchId: number) => {
                const articleInstanceId = reverseMap.get(batchId);
                if (!articleInstanceId || (!isRecursiveSelection && !poiRelatedIds?.has(articleInstanceId))) {
                    return;
                }

                const batchEntry = state.instanceBatchIds.get(articleInstanceId);
                if (!batchEntry) {
                    return;
                }

                const articleId = batchEntry.articleId;

                if (!boxSelectionResult[articleInstanceId]) {
                    let indexMap = articleIndexCache[articleId];
                    if (!indexMap) {
                        indexMap = new Map<string, number>();
                        const keys = Object.keys(state.articles[articleId].articleInstances);
                        for (let i = 0; i < keys.length; i++) {
                            indexMap.set(keys[i], i);
                        }
                        articleIndexCache[articleId] = indexMap;
                    }
                    const index = indexMap.get(articleInstanceId) ?? 0;

                    boxSelectionResult[articleInstanceId] = {
                        articleId,
                        index,
                        meshesCount: 1
                    };
                } else {
                    boxSelectionResult[articleInstanceId].meshesCount += 1;
                }
            });
        });
    }

    let instanceSelections: Record<string, number | undefined> = {};

    if (state.selectionBox.isLeftToRight) {
        Object.entries(boxSelectionResult).forEach(([articleInstanceId, resultData]) => {
            const batchEntry = state.instanceBatchIds.get(articleInstanceId);
            const expectedCount = batchEntry
                ? batchEntry.physicalIds.length + (batchEntry.sheetId !== null ? 1 : 0) + batchEntry.holesIds.length
                : state.articles[resultData.articleId].meshesCount;

            if (resultData.meshesCount === expectedCount) {
                instanceSelections[articleInstanceId] = resultData.index;
            }
        });
    } else {
        instanceSelections = Object.fromEntries(
            Object.entries(boxSelectionResult).map(([articleInstanceId, resultData]) => [
                articleInstanceId,
                resultData.index
            ])
        );
    }

    // Expand selection to include all group members
    const expandedSelections = state.handleGroupSelections(Object.keys(instanceSelections));
    // Merge the expanded selections with the original ones to preserve indices
    for (const [id, idx] of Object.entries(instanceSelections)) {
        if (idx !== undefined) {
            expandedSelections[id] = idx;
        }
    }

    state.setArticleInstanceSelections(expandedSelections);
}

export function isBoxSelectionActive(): boolean {
    return isBoxSelectionActiveFlag;
}

export const createSceneEventHandlerSlice: StateCreator<PlacementState, [], [], SceneEventHandlerSliceState> = (
    _set,
    get
) => ({
    handleOnWindowPointerUp: (event) => {
        if (event.button !== 0 || get().isAutoDragActiveRef.current) {
            return;
        }

        const mainWebGLRendererRef = get().mainWebGLRendererRef;
        if (mainWebGLRendererRef.current && mainWebGLRendererRef.current.domElement.style.cursor !== 'auto') {
            mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
        }

        if (get().isDraggingActive()) {
            get().handleOnDragArticleEnd();
        }

        if (get().isDragNewArticleStartRef.current) {
            get().setIsDragNewArticleStartRef(false);

            if (get().poiSelection.current.filterType === undefined && event.offsetX >= 500) {
                toastNotifications.information({ title: t('info'), message: t('infoSelectPOI') });
            }
        }

        if (get().isArticleDirectlySelectedRef.current) {
            get().setIsArticleDirectlySelectedRef(false);
        }
    },

    handleOnWindowPointerDown: (event) => {
        if (
            event.button !== 0 ||
            !get().isAutoDragActiveRef.current ||
            get().interactionMode === InteractionMode.POI_NAVIGATION
        ) {
            return;
        }

        if ((event.target as HTMLCanvasElement)?.offsetParent?.id !== MAIN_DIV_ID) {
            return;
        }

        // Insert copy of placement indicator per pointer down event
        if (get().isAutoDragActiveRef.current) {
            const state = get();

            const selectedPoiIndex = state.poiSelection.current.index;
            const selectedPoiGuid =
                selectedPoiIndex !== undefined ? state.pois[selectedPoiIndex]?.poi.poiGuid : undefined;
            if (!selectedPoiGuid || selectedPoiIndex === undefined) {
                toastNotifications.information({ title: t('info'), message: t('infoSelectPOI') });
                return;
            }

            const selectionKeys = Object.keys(state.articleInstanceSelections);
            const selectedIds = new Set(selectionKeys);
            if (selectedIds.size === 0) {
                return;
            }

            const isMultiAlignmentInsertion = isMultiPOISelection(state.clipboard);
            if (isMultiAlignmentInsertion) {
                updateInstanceToPoiIndexMap(selectionKeys, state);
            }

            const updatedInstances: Array<{
                articleId: string;
                instanceId: string;
                instance: ReturnType<typeof mapPlacementToArticleInstance>;
            }> = [];
            const pastedGroupMembers = new Map<
                string,
                {
                    name: string;
                    members: GroupMember[];
                }
            >();

            for (const [articleId, dragRef] of state.articleDragRefsMap.current) {
                const instances = dragRef.articleRef.current?.articleInstances;
                if (!instances) {
                    continue;
                }

                for (const [instanceId, instanceData] of Object.entries(instances)) {
                    if (!selectedIds.has(instanceId)) {
                        continue;
                    }

                    const newInstanceId = uuidv4();

                    const sourceGroupId = state.instanceToGroupMap[instanceId];
                    if (sourceGroupId) {
                        const sourceGroup = state.articleGroups[sourceGroupId];
                        if (sourceGroup) {
                            const existing = pastedGroupMembers.get(sourceGroupId) ?? {
                                name: sourceGroup.name,
                                members: []
                            };
                            existing.members.push({
                                instanceId: newInstanceId,
                                articleId
                            });
                            pastedGroupMembers.set(sourceGroupId, existing);
                        }
                    }

                    const poiGuids = isMultiAlignmentInsertion
                        ? instanceData.poiGuids?.filter(Boolean)
                        : getRelatedPoiIndices(state.pois, selectedPoiIndex)
                              .map((poiIndex) => state.pois[poiIndex]?.poi.poiGuid)
                              .filter((poiGuid): poiGuid is string => Boolean(poiGuid));
                    const resolvedPoiGuids = poiGuids?.length ? poiGuids : [selectedPoiGuid];

                    const placement = mapArticleInstanceToPlacement(
                        newInstanceId,
                        instanceData,
                        resolvedPoiGuids,
                        true
                    );
                    updatedInstances.push({
                        articleId,
                        instanceId: newInstanceId,
                        instance: mapPlacementToArticleInstance(placement)
                    });
                }
            }

            if (updatedInstances.length === 0) {
                return;
            }

            requestAnimationFrame(() => {
                usePlacementStore.setState({
                    placementUpdate: {
                        event: PlacementUpdateEvent.PLACE,
                        articleInstanceIds: new Set(updatedInstances.map(({ instanceId }) => instanceId))
                    }
                });
            });

            state.updateArticleInstances(updatedInstances, true);

            // Re-create groups
            for (const [, groupData] of pastedGroupMembers) {
                if (groupData.members.length >= 2) {
                    state.createArticleGroup(groupData.name, groupData.members);
                }
            }

            return;
        }
    },

    handleOnWindowKeyDown: (keyEvent) => {
        const target = keyEvent.target as HTMLElement | null;
        const isInputElementActive = isInputElement(target);

        // Ensure that space triggers POI navigation mode instead of focused element actions
        if (keyEvent.code === 'Space' && !isInputElementActive) {
            // Prevent default behavior
            keyEvent.preventDefault();
            keyEvent.stopPropagation();

            if (!get().isSpaceKeyPressed) {
                get().setIsSpaceKeyPressed(true);
                get().setInteractionMode(InteractionMode.POI_NAVIGATION);

                // Discard active transformation
                if ((get().isDraggingActive() && !get().isAutoDragActiveRef.current) || get().arePivotControlsActive) {
                    get().discardArticleInstanceSelection(false);
                }

                // Release focus
                if (target instanceof HTMLElement && typeof target.blur === 'function') {
                    target.blur();
                }
            }

            return;
        }

        // Open help - shortcuts
        if ((keyEvent.code === 'F1' || (keyEvent.shiftKey && keyEvent.key === '?')) && !isInputElementActive) {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();

            get().setIsShortcutsPanelOpen(true);
            get().setSelectedShortcutSection(ShortcutSection.CAMERA);
            return;
        }

        if ((keyEvent.target as HTMLDivElement)?.id !== MAIN_DIV_ID) {
            return;
        }

        if (keyEvent.code === 'KeyL' || keyEvent.code === 'KeyU') {
            get().handleLockCameraControls(keyEvent.code === 'KeyL');
        }

        if (keyEvent.code === 'KeyI' || keyEvent.code === 'KeyU') {
            get().handleLockCameraControls(keyEvent.code === 'KeyI');
        }

        // Toggle POI navigation mode
        if (keyEvent.code === 'KeyP') {
            const currentMode = get().interactionMode;
            get().setInteractionMode(
                currentMode === InteractionMode.POI_NAVIGATION
                    ? InteractionMode.DEFAULT
                    : InteractionMode.POI_NAVIGATION
            );
        }

        // Discard selection/transformation or toggle POI navigation mode
        if (keyEvent.code === 'Escape') {
            // Handle closing of active panels
            if (get().isShortcutsPanelOpen) {
                get().setIsShortcutsPanelOpen(false);
                return;
            }
            if (get().isGroupPanelOpen) {
                get().setIsGroupPanelOpen(false);
                return;
            }
            if (get().isShortcutsPanelOpen) {
                get().setIsShortcutsPanelOpen(false);
                return;
            }

            if (
                get().interactionMode === InteractionMode.POI_NAVIGATION &&
                get().poiSelection.current.index !== undefined
            ) {
                get().setInteractionMode(InteractionMode.DEFAULT);
            } else {
                get().discardArticleInstanceSelection(false);
            }
        }
    },

    handleOnWindowKeyUp: (keyEvent) => {
        // Activate POI navigation mode when 'Alt' key is released
        if (keyEvent.code === 'Space' && get().isSpaceKeyPressed) {
            get().setIsSpaceKeyPressed(false);
            get().setInteractionMode(InteractionMode.DEFAULT);
        }
    },

    handleBoxSelectionStart: (event) => {
        if (
            event.button !== 0 ||
            (event.buttons & 1) === 0 ||
            get().isDragActiveRef.current ||
            get().isAutoDragActiveRef.current ||
            get().isArticleDirectlySelectedRef.current ||
            get().isTransformationActiveRef.current
        ) {
            return;
        }

        if ((event.target as HTMLCanvasElement)?.offsetParent?.id !== MAIN_DIV_ID) {
            return;
        }

        const state = get();
        if (state.selectionBox && state.mainWebGLRendererRef.current) {
            state.selectionBox.batches = {};

            const start = computeNDCPosition(
                event.clientX,
                event.clientY,
                state.mainWebGLRendererRef.current!.domElement
            );
            if (start) {
                state.selectionBox.startPoint.copy(start);
                isBoxSelectionActiveFlag = true;
                boxSelectionStartClientX = event.clientX;
                boxSelectionStartClientY = event.clientY;
            }
        }

        if (state.isAutoDragActiveRef.current) {
            return;
        }

        state.discardArticleInstanceSelection(false);
    },

    handleBoxSelection: (event) => {
        if ((event.buttons & 1) === 0 || get().isDraggingActive()) {
            return;
        }

        const state = get();
        if (!state.selectionBox || !state.mainWebGLRendererRef.current) {
            return;
        }

        const end = computeNDCPosition(event.clientX, event.clientY, state.mainWebGLRendererRef.current.domElement);
        if (end) {
            state.selectionBox.endPoint.copy(end);
        }

        // Handle selection: throttle selection only once per animation frame - not for every pointer move
        if (isBoxSelectionActiveFlag && animationFrameId === null) {
            const dx = event.clientX - boxSelectionStartClientX;
            const dy = event.clientY - boxSelectionStartClientY;
            if (dx * dx + dy * dy >= boxSelectionStartThreshold * boxSelectionStartThreshold) {
                const isRecursive = event.ctrlKey;
                animationFrameId = requestAnimationFrame(() => {
                    animationFrameId = null;
                    applyBoxSelection(get(), isRecursive);
                });
            }
        }
    },

    handleBoxSelectionEnd: (event) => {
        const state = get();
        if (event.button !== 0 || !isBoxSelectionActiveFlag || !state.selectionBox) {
            return;
        }

        // Cancel any pending selection
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }

        // check whether the pointer was pressed between pointer down and up events to avoid triggering box selection by simple scene clicks
        const dx = event.clientX - boxSelectionStartClientX;
        const dy = event.clientY - boxSelectionStartClientY;
        if (Math.sqrt(dx * dx + dy * dy) < boxSelectionStartThreshold) {
            isBoxSelectionActiveFlag = false;
            return;
        }

        if (!state.mainCameraControlsRef.current?.camera || !state.mainWebGLRendererRef.current) {
            return;
        }

        const end = computeNDCPosition(event.clientX, event.clientY, state.mainWebGLRendererRef.current.domElement);
        if (!end || state.selectionBox.startPoint.equals(end)) {
            isBoxSelectionActiveFlag = false;
            return;
        }

        state.selectionBox.endPoint.copy(end);
        isBoxSelectionActiveFlag = false;

        applyBoxSelection(state, event.ctrlKey);
    },

    initializeSelectionBox: (scene) => {
        const state = get();
        if (!state.mainCameraControlsRef.current || !scene || !state.mainWebGLRendererRef.current) {
            return;
        }

        const camera = state.mainCameraControlsRef.current.camera;
        state.selectionBox = new SelectionBox(camera, scene);
    },

    isSelectionHelperEnabled: (event) => {
        const state = get();
        return (
            !state.isArticleDirectlySelectedRef.current &&
            !state.isDragActiveRef.current &&
            !state.isDragNewArticleActiveRef.current &&
            !state.isDragNewArticleStartRef.current &&
            !state.isAutoDragActiveRef.current &&
            !state.isTransformationActiveRef.current &&
            (event.button === 0 || (event.buttons & 1) !== 0)
        );
    }
});
